/**
 * Serverzustand der Automatisierung.
 *
 * Regeln, Zeitplaene und Wartungsplaene werden einmal eingerichtet und laufen
 * dann -- sie halten ihre Daten laenger als der Standard.
 *
 * **Zwei Ausnahmen, und beide aus demselben Grund:** Nach einem Lauf
 * (`useRunJobs`, `useRunRecurring`) und nach einer Zustellung aendern sich
 * Trefferzaehler, Termine und Fehlermeldungen. Wer den Lauf ausloest, will
 * sehen, was er bewirkt hat -- deshalb werden die Listen danach neu geholt,
 * auch die der Tickets: Ein Wartungsplan hat gerade eines angelegt.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../../lib/apiError';
import { ticketKeys } from '../tickets/queries';
import {
  createJob,
  createRecurring,
  createRule,
  createWebhook,
  deleteJob,
  deleteRecurring,
  deleteRule,
  deleteWebhook,
  deliverWebhooks,
  listDeliveries,
  listJobs,
  listRecurring,
  listRuleFields,
  listRules,
  listWebhookEvents,
  listWebhooks,
  runJobs,
  runRecurring,
  testRule,
  updateJob,
  updateRecurring,
  updateRule,
  updateWebhook,
  type AutomationRuleCreate,
  type AutomationRuleUpdate,
  type RecurringTicketCreate,
  type RecurringTicketUpdate,
  type ScheduledJobCreate,
  type ScheduledJobUpdate,
  type WebhookCreate,
  type WebhookUpdate,
} from './api';

export const automationKeys = {
  rules: ['automation-rules'] as const,
  ruleFields: ['automation-rules', 'fields'] as const,
  jobs: ['scheduled-jobs'] as const,
  recurring: ['recurring-tickets'] as const,
  webhooks: ['webhooks'] as const,
  webhookEvents: ['webhook-events'] as const,
  deliveries: (webhookId: string) => ['webhooks', webhookId, 'deliveries'] as const,
};

const retryUnlessPermanent = (failureCount: number, error: unknown) =>
  !(error instanceof ApiError && error.isPermanent) && failureCount < 2;

/** Fuenf Minuten -- wie bei den uebrigen Einstellungen. */
const SETTINGS_STALE_TIME = 5 * 60 * 1000;

export function useRules() {
  return useQuery({
    queryKey: automationKeys.rules,
    queryFn: listRules,
    staleTime: SETTINGS_STALE_TIME,
    retry: retryUnlessPermanent,
  });
}

export function useRuleFields() {
  return useQuery({
    queryKey: automationKeys.ruleFields,
    queryFn: listRuleFields,
    staleTime: SETTINGS_STALE_TIME,
    retry: retryUnlessPermanent,
  });
}

export function useJobs() {
  return useQuery({
    queryKey: automationKeys.jobs,
    queryFn: listJobs,
    staleTime: SETTINGS_STALE_TIME,
    retry: retryUnlessPermanent,
  });
}

export function useRecurring() {
  return useQuery({
    queryKey: automationKeys.recurring,
    queryFn: listRecurring,
    staleTime: SETTINGS_STALE_TIME,
    retry: retryUnlessPermanent,
  });
}

export function useWebhooks() {
  return useQuery({
    queryKey: automationKeys.webhooks,
    queryFn: listWebhooks,
    staleTime: SETTINGS_STALE_TIME,
    retry: retryUnlessPermanent,
  });
}

export function useWebhookEvents() {
  return useQuery({
    queryKey: automationKeys.webhookEvents,
    queryFn: listWebhookEvents,
    staleTime: SETTINGS_STALE_TIME,
    retry: retryUnlessPermanent,
  });
}

export function useDeliveries(webhookId: string | null) {
  return useQuery({
    queryKey: automationKeys.deliveries(webhookId ?? 'none'),
    queryFn: () => listDeliveries(webhookId ?? ''),
    enabled: webhookId !== null,
    retry: retryUnlessPermanent,
  });
}

function useInvalidate(key: readonly unknown[]) {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({ queryKey: key });
  };
}

// -- Regeln -------------------------------------------------------------------

export function useCreateRule() {
  const invalidate = useInvalidate(automationKeys.rules);
  return useMutation({
    mutationFn: (payload: AutomationRuleCreate) => createRule(payload),
    onSuccess: invalidate,
  });
}

export function useUpdateRule() {
  const invalidate = useInvalidate(automationKeys.rules);
  return useMutation({
    mutationFn: ({ ruleId, payload }: { ruleId: string; payload: AutomationRuleUpdate }) =>
      updateRule(ruleId, payload),
    onSuccess: invalidate,
  });
}

export function useDeleteRule() {
  const invalidate = useInvalidate(automationKeys.rules);
  return useMutation({
    mutationFn: (ruleId: string) => deleteRule(ruleId),
    onSuccess: invalidate,
  });
}

export function useTestRule() {
  return useMutation({
    mutationFn: ({ ruleId, ticketId }: { ruleId: string; ticketId: string }) =>
      testRule(ruleId, ticketId),
  });
}

// -- Zeitplaene ---------------------------------------------------------------

export function useCreateJob() {
  const invalidate = useInvalidate(automationKeys.jobs);
  return useMutation({
    mutationFn: (payload: ScheduledJobCreate) => createJob(payload),
    onSuccess: invalidate,
  });
}

export function useUpdateJob() {
  const invalidate = useInvalidate(automationKeys.jobs);
  return useMutation({
    mutationFn: ({ jobId, payload }: { jobId: string; payload: ScheduledJobUpdate }) =>
      updateJob(jobId, payload),
    onSuccess: invalidate,
  });
}

export function useDeleteJob() {
  const invalidate = useInvalidate(automationKeys.jobs);
  return useMutation({
    mutationFn: (jobId: string) => deleteJob(jobId),
    onSuccess: invalidate,
  });
}

export function useRunJobs() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: runJobs,
    onSuccess: async () => {
      // Beides: Der Lauf hat Tickets angefasst und die Auftraege vermerkt.
      await queryClient.invalidateQueries({ queryKey: automationKeys.jobs });
      await queryClient.invalidateQueries({ queryKey: ticketKeys.all });
    },
  });
}

// -- Wartungsplaene -----------------------------------------------------------

export function useCreateRecurring() {
  const invalidate = useInvalidate(automationKeys.recurring);
  return useMutation({
    mutationFn: (payload: RecurringTicketCreate) => createRecurring(payload),
    onSuccess: invalidate,
  });
}

export function useUpdateRecurring() {
  const invalidate = useInvalidate(automationKeys.recurring);
  return useMutation({
    mutationFn: ({
      recurringId,
      payload,
    }: {
      recurringId: string;
      payload: RecurringTicketUpdate;
    }) => updateRecurring(recurringId, payload),
    onSuccess: invalidate,
  });
}

export function useDeleteRecurring() {
  const invalidate = useInvalidate(automationKeys.recurring);
  return useMutation({
    mutationFn: (recurringId: string) => deleteRecurring(recurringId),
    onSuccess: invalidate,
  });
}

export function useRunRecurring() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: runRecurring,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: automationKeys.recurring });
      await queryClient.invalidateQueries({ queryKey: ticketKeys.all });
    },
  });
}

// -- Webhooks -----------------------------------------------------------------

export function useCreateWebhook() {
  const invalidate = useInvalidate(automationKeys.webhooks);
  return useMutation({
    mutationFn: (payload: WebhookCreate) => createWebhook(payload),
    onSuccess: invalidate,
  });
}

export function useUpdateWebhook() {
  const invalidate = useInvalidate(automationKeys.webhooks);
  return useMutation({
    mutationFn: ({ webhookId, payload }: { webhookId: string; payload: WebhookUpdate }) =>
      updateWebhook(webhookId, payload),
    onSuccess: invalidate,
  });
}

export function useDeleteWebhook() {
  const invalidate = useInvalidate(automationKeys.webhooks);
  return useMutation({
    mutationFn: (webhookId: string) => deleteWebhook(webhookId),
    onSuccess: invalidate,
  });
}

export function useDeliverWebhooks() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deliverWebhooks,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: automationKeys.webhooks });
      await queryClient.invalidateQueries({ queryKey: ['webhooks'] });
    },
  });
}
